/**
 * Answer attempts and the host's answer to them (agion/Agion#2767, owner
 * amendment A3): the rules chat-ui and the admin-ui run console share.
 *
 * Every answer a browser sends carries an answer_attempt_id. AnswerAttempts
 * decides which: a fresh UUID for every submission, except that a user who
 * sends an unchanged answer again after its delivery stayed uncertain (no
 * receipt in time, or a result with outcome unknown) repeats that attempt, so
 * the host can recognise it. An edited answer, or one sent after a definitive
 * refusal or receipt, gets a fresh attempt. Nothing here sends anything: a
 * retry is always the user's own send.
 *
 * A host that admits answers (capability question_answer_admission_v1)
 * answers each attempt with mission.question.answer.result and ends a
 * question with mission.question.closed. questionAnswerEvent() turns a
 * result into the delivery event of deliveryState.ts. A refusal, whatever
 * its reason, refuses only that attempt: only the closed frame, or the run
 * ending, closes a question (missionstream_question.go).
 * An older host sends neither frame, and the relay's echo and delivery_failed
 * error stay the receipt and the refusal.
 *
 * The texts are presentation only: the host decides every outcome and
 * reason. Keys are relative; each app reads them in its own namespace.
 */

import {
  QuestionAnswerReasons,
  QuestionClosedReasons,
  type QuestionAnswerOutcome,
  type QuestionAnswerReason,
  type QuestionClosedReason,
} from './generated/missionstream'
import type { DeliveryEvent } from './deliveryState'
import { unknownKind } from './messagePlacement'

const HEX = '0123456789abcdef'

/** A fresh answer attempt id: a random version 4 UUID. */
export function newAnswerAttemptId(): string {
  const crypto = globalThis.crypto
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  // randomUUID exists only in a secure context; getRandomValues everywhere.
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6]! & 0x0f) | 0x40
  bytes[8] = (bytes[8]! & 0x3f) | 0x80
  let out = ''
  for (let i = 0; i < bytes.length; i++) {
    if (i === 4 || i === 6 || i === 8 || i === 10) out += '-'
    out += HEX[bytes[i]! >> 4]! + HEX[bytes[i]! & 0x0f]!
  }
  return out
}

/** What an answer says: an unchanged retry repeats all of it. */
export interface AnswerIntent {
  requestId: string
  text: string
  /** The host file ids of its attachments, in order. */
  fileIds: readonly string[]
  /** An approval card's explicit decision. */
  decision?: string
  /** A structured answer's option ids, in the order sent. */
  selectedOptionIds?: readonly string[]
  /** A structured answer's Other text. */
  otherText?: string
}

function sameList(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((id, i) => id === b[i])
}

function sameIntent(a: AnswerIntent, b: AnswerIntent): boolean {
  return a.requestId === b.requestId && a.text === b.text && (a.decision ?? '') === (b.decision ?? '') &&
    sameList(a.fileIds, b.fileIds) && sameList(a.selectedOptionIds ?? [], b.selectedOptionIds ?? []) &&
    (a.otherText ?? '') === (b.otherText ?? '')
}

/** What a delivery event says about the attempt it happened to. */
export type AttemptEnding = 'uncertain' | 'settled' | 'unchanged'

/**
 * uncertain: the attempt may have reached the host (no receipt in time, or
 * the host could not confirm it), so an unchanged retry repeats it. settled:
 * received or refused, so the next send is a new attempt. unchanged: nothing
 * happened to the attempt itself, as when the frame never left the browser.
 */
export function attemptEnding(event: DeliveryEvent): AttemptEnding {
  switch (event) {
    case 'timeout':
    case 'unconfirmed':
      return 'uncertain'
    case 'receipt':
    case 'refusal':
      return 'settled'
    case 'sent':
    case 'not_sent':
    case 'accepted':
    case 'undelivered':
      return 'unchanged'
    default:
      return unknownKind(event, 'unchanged')
  }
}

/**
 * The attempt ids of one chat's answers. Keeps, per question, the latest
 * attempt whose delivery is uncertain, with what it said.
 */
export class AnswerAttempts {
  private readonly uncertain = new Map<string, { attemptId: string; intent: AnswerIntent }>()
  private readonly mint: () => string

  constructor(mint: () => string = newAnswerAttemptId) {
    this.mint = mint
  }

  /** The attempt id to send this answer with now. */
  attemptFor(intent: AnswerIntent): string {
    const kept = this.uncertain.get(intent.requestId)
    if (kept && sameIntent(kept.intent, intent)) return kept.attemptId
    return this.mint()
  }

  /** Records what an event did to an attempt sent with this intent. */
  record(intent: AnswerIntent, attemptId: string, event: DeliveryEvent): void {
    switch (attemptEnding(event)) {
      case 'uncertain':
        this.uncertain.set(intent.requestId, { attemptId, intent: { ...intent, fileIds: [...intent.fileIds], ...(intent.selectedOptionIds ? { selectedOptionIds: [...intent.selectedOptionIds] } : {}) } })
        return
      case 'settled':
        if (this.uncertain.get(intent.requestId)?.attemptId === attemptId) this.uncertain.delete(intent.requestId)
        return
      case 'unchanged':
        return
    }
  }

  /** Forgets a question's uncertain attempt, as when the question closed. */
  forget(requestId: string): void {
    this.uncertain.delete(requestId)
  }

  /** Forgets every attempt, for a new execution. */
  clear(): void {
    this.uncertain.clear()
  }
}

/**
 * The delivery event a result is: accepted is the receipt, refused a refusal
 * and unknown leaves it unconfirmed. An outcome this build does not know is
 * no event (null).
 */
export function questionAnswerEvent(outcome: QuestionAnswerOutcome): DeliveryEvent | null {
  switch (outcome) {
    case 'accepted':
      return 'receipt'
    case 'refused':
      return 'refusal'
    case 'unknown':
      return 'unconfirmed'
    default:
      return unknownKind(outcome, null)
  }
}

/** Whether this build knows the reason; a newer host may send one it does not. */
export function isQuestionAnswerReason(reason: unknown): reason is QuestionAnswerReason {
  return QuestionAnswerReasons.some((row) => row.value === reason)
}

/** Whether this build knows the closure reason. */
export function isQuestionClosedReason(reason: unknown): reason is QuestionClosedReason {
  return QuestionClosedReasons.some((row) => row.value === reason)
}

/** How a closed question's card reads: expired, or closed. */
export function questionClosedStatus(reason: QuestionClosedReason): 'expired' | 'closed' {
  switch (reason) {
    case 'expired':
      return 'expired'
    case 'cancelled':
    case 'publication_failed':
    case 'execution_ended':
      return 'closed'
    default:
      return unknownKind(reason, 'closed')
  }
}

/** A text as an app reads it: a key relative to its namespace, and the English fallback. */
export interface QuestionCopy {
  key: string
  fallback: string
}

/** Why the host refused an answer, as the person who sent it reads it. */
export const QUESTION_ANSWER_REASON_TEXTS: Record<QuestionAnswerReason, QuestionCopy> = {
  answer_in_progress: {
    key: 'questionAnswerReason.answerInProgress',
    fallback: 'Another answer to this question is still being checked. Wait a moment and try again.',
  },
  answer_admission_unavailable: {
    key: 'questionAnswerReason.answerAdmissionUnavailable',
    fallback: 'The host could not take the answer in time. Try again.',
  },
  invalid_answer: {
    key: 'questionAnswerReason.invalidAnswer',
    fallback: 'The answer could not be read. Check it and try again.',
  },
  attempt_conflict: {
    key: 'questionAnswerReason.attemptConflict',
    fallback: 'This answer differs from the earlier send it repeats. Send it again.',
  },
  question_closed: {
    key: 'questionAnswerReason.questionClosed',
    fallback: 'This question is no longer open.',
  },
  confirmation_unavailable: {
    key: 'questionAnswerReason.confirmationUnavailable',
    fallback: 'The host can no longer confirm whether it got this answer.',
  },
  sender_not_allowed: {
    key: 'questionAnswerReason.senderNotAllowed',
    fallback: 'You are not allowed to answer this question.',
  },
  run_not_live: {
    key: 'questionAnswerReason.runNotLive',
    fallback: 'The run is not running any more.',
  },
  attachment_invalid: {
    key: 'questionAnswerReason.attachmentInvalid',
    fallback: 'The attached files could not be verified. Remove and attach them again.',
  },
  attachment_host_mismatch: {
    key: 'questionAnswerReason.attachmentHostMismatch',
    fallback: "These files were uploaded to another server. Attach them to this run's server.",
  },
  attachment_host_ambiguous: {
    key: 'questionAnswerReason.attachmentHostAmbiguous',
    fallback: 'The server that holds these files could not be verified. Attach the files again.',
  },
  attachment_too_many: {
    key: 'questionAnswerReason.attachmentTooMany',
    fallback: 'This answer has too many files. Remove some files and try again.',
  },
  attachment_unavailable: {
    key: 'questionAnswerReason.attachmentUnavailable',
    fallback: 'The attached files are unavailable. Try again, or remove and attach them again.',
  },
}

/** Why a question closed, as its card says. */
export const QUESTION_CLOSED_TEXTS: Record<QuestionClosedReason, QuestionCopy> = {
  cancelled: {
    key: 'questionClosed.cancelled',
    fallback: 'This question was withdrawn.',
  },
  expired: {
    key: 'questionClosed.expired',
    fallback: 'This question expired.',
  },
  publication_failed: {
    key: 'questionClosed.publicationFailed',
    fallback: 'This question could not be shown, so it was closed.',
  },
  execution_ended: {
    key: 'questionClosed.executionEnded',
    fallback: 'This question closed when the run ended.',
  },
}

/** What an answer the host could not confirm says (outcome unknown). */
export const QUESTION_ANSWER_UNCONFIRMED_TEXT: QuestionCopy = {
  key: 'questionAnswer.unconfirmed',
  fallback: 'Delivery not confirmed: the host could not confirm that it got this answer.',
}
